import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Button, Alert, Table, Spinner, Row, Col, Badge, Modal, Form, InputGroup, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../App.css';

interface Membership {
  season: number;
  leagueId: string;
  leagueName: string;
  userName: string;
  survivorName: string;
  realName: string;
  paymentStatus: 'pending' | 'marked_paid' | 'verified' | string;
  paymentMethod: string;
  paymentAmount?: number;
  paymentReportedAt?: string;
  paymentVerifiedAt?: string;
  hasDraft: boolean;
  isCommissioner: boolean;
}

interface UnifiedUser {
  id: string;
  email: string;
  primaryName: string;
  realNames: string[];
  isAdmin: boolean;
  hasPassword: boolean;
  seasons: number[];
  memberships: Membership[];
}

const AdminUserManagement: React.FC = () => {
  const [users, setUsers] = useState<UnifiedUser[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([49, 50, 51]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeasonFilter, setSelectedSeasonFilter] = useState<string>('all'); // 'all' | '51' | '50' | '49'
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending_payment' | 'no_draft' | 'admin_only'>('all');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  // Password reset modal state
  const [resetTargetUser, setResetTargetUser] = useState<{ email: string; name: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  const fetchUnifiedUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/all-unified`);
      if (!response.ok) throw new Error('Failed to fetch user directory');
      const data = await response.json();
      setUsers(data.users || []);
      if (Array.isArray(data.availableSeasons)) {
        setAvailableSeasons(data.availableSeasons.sort((a: number, b: number) => b - a));
      }
    } catch (err: any) {
      setError(err.message || 'Could not load users.');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnifiedUsers();
  }, [fetchUnifiedUsers]);

  // Handle Login As for a specific season membership
  const handleLoginAs = (membership: Membership, user: UnifiedUser) => {
    const seasonStr = String(membership.season);
    const userInfo = {
      userName: membership.userName,
      survivorName: membership.survivorName,
      realName: membership.realName,
      email: user.email,
      leagueId: membership.leagueId,
      paymentMethod: membership.paymentMethod,
      isAdmin: user.isAdmin,
      season: membership.season
    };

    localStorage.setItem(`fantasyUser_${seasonStr}`, JSON.stringify(userInfo));
    localStorage.setItem('selectedSeason', seasonStr);
    if (user.isAdmin) {
      localStorage.setItem('isAdminAuthenticated', 'true');
    }

    window.dispatchEvent(new Event('seasonChanged'));
    window.location.href = `/league/${membership.leagueId}`;
  };

  // Payment Status Toggle
  const handleUpdatePaymentStatus = async (leagueId: string, userName: string, newStatus: 'pending' | 'marked_paid' | 'verified') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/members/${encodeURIComponent(userName)}/payment-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentStatus: newStatus }),
      });
      if (!response.ok) throw new Error('Failed to update payment status');
      setMessage(`Updated ${userName} payment status to ${newStatus}`);
      fetchUnifiedUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update payment status');
    }
  };

  // Remove Member from specific league
  const handleRemoveMember = async (leagueId: string, userName: string, season: number) => {
    if (!window.confirm(`Are you sure you want to remove entry "${userName}" from Season ${season}? This will permanently delete this member registration and any draft associated with it.`)) {
      return;
    }
    setIsLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/members/${encodeURIComponent(userName)}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to remove entry');
      }
      setMessage(`Entry "${userName}" removed from Season ${season} successfully.`);
      await fetchUnifiedUsers();
    } catch (err: any) {
      setError(err.message || 'Error removing entry.');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle Admin Status
  const handleToggleAdmin = async (user: UnifiedUser) => {
    const isCurrentlyAdmin = user.isAdmin;
    const actionText = isCurrentlyAdmin ? 'remove admin privileges from' : 'grant admin privileges to';
    if (!window.confirm(`Are you sure you want to ${actionText} ${user.primaryName} (${user.email || 'No email'})?`)) {
      return;
    }

    setIsLoading(true);
    setMessage(null);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/toggle-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          userName: user.primaryName,
          isAdmin: !isCurrentlyAdmin
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update admin status');

      setMessage(data.message);
      await fetchUnifiedUsers();
    } catch (err: any) {
      setError(err.message || 'Error updating admin status');
    } finally {
      setIsLoading(false);
    }
  };

  // Password Reset Modal Handlers
  const handleOpenResetModal = (user: UnifiedUser) => {
    setResetTargetUser({ email: user.email, name: user.primaryName });
    setNewPassword('');
    setShowPassword(false);
    setResetError(null);
    setResetSuccess(null);
    setGeneratedLink(null);
  };

  const handleDirectPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetTargetUser) return;
    if (!newPassword || newPassword.trim().length < 4) {
      setResetError('Password must be at least 4 characters long.');
      return;
    }

    setIsResetting(true);
    setResetError(null);
    setResetSuccess(null);
    setGeneratedLink(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetTargetUser.email,
          userName: resetTargetUser.name,
          newPassword: newPassword.trim()
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to reset password');

      setResetSuccess(data.message || 'Password successfully updated.');
      setNewPassword('');
    } catch (err: any) {
      setResetError(err.message || 'Error setting password');
    } finally {
      setIsResetting(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!resetTargetUser) return;
    if (!resetTargetUser.email) {
      setResetError('This member has no email address on file.');
      return;
    }

    setIsResetting(true);
    setResetError(null);
    setResetSuccess(null);
    setGeneratedLink(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetTargetUser.email,
          userName: resetTargetUser.name,
          sendEmail: true
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send reset email');

      setResetSuccess(data.message || 'Password reset email sent.');
      if (data.resetLink) {
        setGeneratedLink(data.resetLink);
      }
    } catch (err: any) {
      setResetError(err.message || 'Error sending reset email');
    } finally {
      setIsResetting(false);
    }
  };

  // Export Emails CSV
  const handleExportEmails = (targetSeason?: number) => {
    let exportList = users;
    if (targetSeason) {
      exportList = users.filter(u => u.seasons.includes(targetSeason));
    } else if (selectedSeasonFilter !== 'all') {
      const sNum = parseInt(selectedSeasonFilter, 10);
      exportList = users.filter(u => u.seasons.includes(sNum));
    }

    const rows = ['Primary Name,Email,Real Names,Seasons Enrolled'];
    exportList.forEach(u => {
      if (!u.email) return;
      const cleanName = u.primaryName.includes(',') ? `"${u.primaryName}"` : u.primaryName;
      const cleanReal = u.realNames.join(' / ').replace(/"/g, '""');
      const seasonsStr = u.seasons.map(s => `S${s}`).join(' ');
      rows.push(`${cleanName},${u.email},"${cleanReal}",${seasonsStr}`);
    });

    if (rows.length <= 1) {
      alert('No user emails found to export.');
      return;
    }

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', targetSeason ? `survivor_emails_season_${targetSeason}.csv` : 'survivor_all_users_directory.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered Users List
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // 1. Season filter
      if (selectedSeasonFilter !== 'all') {
        const seasonNum = parseInt(selectedSeasonFilter, 10);
        if (!user.seasons.includes(seasonNum)) return false;
      }

      // 2. Status filter
      if (statusFilter === 'pending_payment') {
        const hasPending = user.memberships.some(m => m.paymentStatus !== 'verified');
        if (!hasPending) return false;
      } else if (statusFilter === 'no_draft') {
        const hasNoDraft = user.memberships.some(m => !m.hasDraft);
        if (!hasNoDraft) return false;
      } else if (statusFilter === 'admin_only') {
        if (!user.isAdmin) return false;
      }

      // 3. Search query
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        const matchesName = user.primaryName.toLowerCase().includes(q);
        const matchesEmail = user.email.toLowerCase().includes(q);
        const matchesReal = user.realNames.some(rn => rn.toLowerCase().includes(q));
        const matchesMemberUser = user.memberships.some(m => m.userName.toLowerCase().includes(q) || m.survivorName.toLowerCase().includes(q));
        if (!matchesName && !matchesEmail && !matchesReal && !matchesMemberUser) return false;
      }

      return true;
    });
  }, [users, selectedSeasonFilter, statusFilter, searchTerm]);

  // Aggregate stats
  const stats = useMemo(() => {
    const totalUsers = users.length;
    const s51Count = users.filter(u => u.seasons.includes(51)).length;
    const s51Drafts = users.filter(u => u.memberships.some(m => m.season === 51 && m.hasDraft)).length;
    const pendingCount = users.filter(u => u.memberships.some(m => m.season === 51 && m.paymentStatus !== 'verified')).length;
    return { totalUsers, s51Count, s51Drafts, pendingCount };
  }, [users]);

  return (
    <Container className="mt-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
        <Dropdown>
          <Dropdown.Toggle variant="outline-success" size="sm" id="export-dropdown">
            📥 Export Email Directory
          </Dropdown.Toggle>
          <Dropdown.Menu align="end">
            <Dropdown.Item onClick={() => handleExportEmails()}>Export All Seasons (CSV)</Dropdown.Item>
            <Dropdown.Divider />
            {availableSeasons.map(s => (
              <Dropdown.Item key={s} onClick={() => handleExportEmails(s)}>
                Export Season {s} Members Only
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Top Stats Overview */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <Card className="stat-card-dark stat-card-total text-center p-3 h-100">
            <div className="small text-uppercase fw-bold" style={{ color: '#94a3b8' }}>Total Users</div>
            <div className="fs-2 fw-bold text-warning">{stats.totalUsers}</div>
            <div className="small text-light opacity-75">All Seasons Directory</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card-dark stat-card-s51 text-center p-3 h-100">
            <div className="small text-uppercase fw-bold" style={{ color: '#a7f3d0' }}>Season 51 Signed Up</div>
            <div className="fs-2 fw-bold text-white">{stats.s51Count}</div>
            <div className="small text-light opacity-75">Current Season Players</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card-dark stat-card-drafts text-center p-3 h-100">
            <div className="small text-uppercase fw-bold" style={{ color: '#bae6fd' }}>S51 Drafts Locked</div>
            <div className="fs-2 fw-bold text-white">{stats.s51Drafts}/{stats.s51Count}</div>
            <div className="small text-light opacity-75">Completed 5-Player Rosters</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card-dark stat-card-pending text-center p-3 h-100">
            <div className="small text-uppercase fw-bold" style={{ color: '#fed7aa' }}>S51 Pending Payment</div>
            <div className="fs-2 fw-bold text-white">{stats.pendingCount}</div>
            <div className="small text-light opacity-75">Unverified Entries</div>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm home-card border-0">
        <Card.Header as="h4" className="card-title-custom d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>👥 Unified User Management (All Seasons)</span>
          <span className="badge bg-secondary fw-normal fs-6">
            Showing {filteredUsers.length} of {users.length} Users
          </span>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {message && <Alert variant="success" dismissible onClose={() => setMessage(null)}>{message}</Alert>}

          {/* Search and Filters Bar */}
          <div className="p-3 mb-4 rounded border bg-light">
            <Row className="g-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="🔍 Search by Member Name, Real Name, or Email..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-center gap-2">
                  <span className="small fw-bold text-muted text-nowrap">Filter Season:</span>
                  <Form.Select
                    size="sm"
                    value={selectedSeasonFilter}
                    onChange={e => setSelectedSeasonFilter(e.target.value)}
                  >
                    <option value="all">🌟 All Seasons ({users.length})</option>
                    {availableSeasons.map(s => {
                      const count = users.filter(u => u.seasons.includes(s)).length;
                      return <option key={s} value={String(s)}>Season {s} ({count} players)</option>;
                    })}
                  </Form.Select>
                </div>
              </Col>
              <Col md={3}>
                <div className="d-flex align-items-center gap-2">
                  <span className="small fw-bold text-muted text-nowrap">Status:</span>
                  <Form.Select
                    size="sm"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value as any)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending_payment">Pending Payment</option>
                    <option value="no_draft">No Draft Submitted</option>
                    <option value="admin_only">Admins Only 👑</option>
                  </Form.Select>
                </div>
              </Col>
            </Row>
          </div>

          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
              <p className="text-muted mt-2">Loading complete user directory...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <Alert variant="secondary" className="text-center p-4">
              No users matching your search filters.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ minWidth: '180px' }}>User / Member</th>
                    <th style={{ minWidth: '180px' }}>Email</th>
                    <th style={{ minWidth: '140px' }}>Real Names</th>
                    <th style={{ minWidth: '220px' }}>Enrolled Seasons & Log In As</th>
                    <th style={{ minWidth: '180px' }}>Account & Global Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const isExpanded = expandedUserId === user.id;

                    return (
                      <React.Fragment key={user.id}>
                        <tr className={user.isAdmin ? 'table-warning-subtle' : undefined}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <strong>{user.primaryName || user.email}</strong>
                              {user.isAdmin && (
                                <Badge bg="danger" style={{ fontSize: '0.65rem' }}>
                                  ADMIN 👑
                                </Badge>
                              )}
                            </div>
                            <div className="small text-muted">
                              {user.memberships.length} season registration(s)
                            </div>
                          </td>

                          <td>
                            {user.email ? (
                              <a href={`mailto:${user.email}`} className="text-decoration-none small">
                                {user.email}
                              </a>
                            ) : (
                              <span className="text-muted small">No Email on file</span>
                            )}
                          </td>

                          <td>
                            <div className="small">
                              {user.realNames.length > 0 ? user.realNames.join(', ') : '—'}
                            </div>
                          </td>

                          <td>
                            <div className="d-flex flex-wrap gap-1 align-items-center mb-1">
                              {user.memberships.map(m => {
                                const isVerified = m.paymentStatus === 'verified';
                                const isPaidReported = m.paymentStatus === 'marked_paid';

                                return (
                                  <div
                                    key={`${m.season}-${m.leagueId}-${m.userName}`}
                                    className="d-inline-flex align-items-center gap-1 border rounded p-1 bg-white shadow-sm"
                                  >
                                    <Badge
                                      bg={m.season === 51 ? 'primary' : m.season === 50 ? 'info' : 'secondary'}
                                      className="fw-bold"
                                      style={{ fontSize: '0.7rem' }}
                                    >
                                      S{m.season}
                                    </Badge>
                                    <span
                                      className="small text-truncate"
                                      style={{ maxWidth: '100px', fontSize: '0.78rem' }}
                                      title={`${m.survivorName || m.userName} (${m.leagueName})`}
                                    >
                                      {m.survivorName || m.userName}
                                    </span>
                                    <Badge
                                      bg={isVerified ? 'success' : isPaidReported ? 'info' : 'warning'}
                                      text={isVerified ? 'white' : 'dark'}
                                      style={{ fontSize: '0.62rem' }}
                                      title={m.paymentStatus}
                                    >
                                      {isVerified ? '✓ Paid' : isPaidReported ? 'Reported' : 'Pending'}
                                    </Badge>
                                    {m.season >= 51 && (
                                      <Badge
                                        bg={m.hasDraft ? 'success' : 'danger'}
                                        style={{ fontSize: '0.62rem' }}
                                        title={m.hasDraft ? 'Draft locked in' : 'No draft submitted'}
                                      >
                                        {m.hasDraft ? 'Drafted' : 'No Draft'}
                                      </Badge>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      style={{ fontSize: '0.7rem', padding: '1px 5px' }}
                                      onClick={() => handleLoginAs(m, user)}
                                      title={`Log in as ${m.userName} for Season ${m.season}`}
                                    >
                                      Log In (S{m.season})
                                    </Button>
                                    {!m.isCommissioner && (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        style={{ fontSize: '0.7rem', padding: '1px 5px' }}
                                        onClick={() => handleRemoveMember(m.leagueId, m.userName, m.season)}
                                        title={`Delete entry "${m.userName}" from Season ${m.season}`}
                                      >
                                        🗑️
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none small text-secondary"
                              onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                            >
                              {isExpanded ? '▲ Hide Season Details' : '▼ Manage Payments & Leagues'}
                            </Button>
                          </td>

                          <td>
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="text-dark"
                                style={{ fontSize: '0.78rem' }}
                                onClick={() => handleOpenResetModal(user)}
                                title="Set password directly or send reset email"
                              >
                                🔑 Reset PW
                              </Button>

                              <Button
                                variant={user.isAdmin ? 'outline-dark' : 'outline-primary'}
                                size="sm"
                                style={{ fontSize: '0.78rem' }}
                                onClick={() => handleToggleAdmin(user)}
                                title={user.isAdmin ? 'Revoke Admin Privileges' : 'Make this user an Admin'}
                              >
                                {user.isAdmin ? '👑 Revoke Admin' : '👑 Make Admin'}
                              </Button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded details row */}
                        {isExpanded && (
                          <tr className="bg-light">
                            <td colSpan={5} className="p-3">
                              <div className="p-3 bg-white rounded border shadow-sm">
                                <h6 className="fw-bold mb-3 text-primary">
                                  Season Registrations & Payments for {user.primaryName} ({user.email || 'No email'})
                                </h6>
                                <div className="table-responsive">
                                  <Table size="sm" bordered hover className="mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Season</th>
                                        <th>League</th>
                                        <th>Survivor / Member Name</th>
                                        <th>Real Name</th>
                                        <th>Draft Status</th>
                                        <th>Payment Status</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {user.memberships.map(m => (
                                        <tr key={`expanded-${m.season}-${m.leagueId}-${m.userName}`}>
                                          <td><Badge bg="dark">Season {m.season}</Badge></td>
                                          <td>{m.leagueName}</td>
                                          <td><strong>{m.survivorName || m.userName}</strong></td>
                                          <td>{m.realName || '—'}</td>
                                          <td>
                                            {m.hasDraft ? (
                                              <Badge bg="success">✓ Draft Submitted</Badge>
                                            ) : (
                                              <Badge bg="danger">❌ No Draft</Badge>
                                            )}
                                          </td>
                                          <td>
                                            <div className="d-flex align-items-center gap-2">
                                              {m.paymentStatus === 'verified' ? (
                                                <Badge bg="success">Verified Paid</Badge>
                                              ) : m.paymentStatus === 'marked_paid' ? (
                                                <Badge bg="info" className="text-dark">User Reported Paid</Badge>
                                              ) : (
                                                <Badge bg="warning" className="text-dark">Pending (${m.paymentAmount || 11})</Badge>
                                              )}
                                              <small className="text-muted">({m.paymentMethod || 'Venmo'})</small>
                                            </div>
                                            {m.paymentReportedAt && (
                                              <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                Reported: {new Date(m.paymentReportedAt).toLocaleDateString()}
                                              </div>
                                            )}
                                          </td>
                                          <td>
                                            <div className="d-flex gap-1 flex-wrap">
                                              {m.paymentStatus !== 'verified' ? (
                                                <Button
                                                  size="sm"
                                                  variant="outline-success"
                                                  onClick={() => handleUpdatePaymentStatus(m.leagueId, m.userName, 'verified')}
                                                >
                                                  ✓ Verify Paid
                                                </Button>
                                              ) : (
                                                <Button
                                                  size="sm"
                                                  variant="outline-secondary"
                                                  onClick={() => handleUpdatePaymentStatus(m.leagueId, m.userName, 'pending')}
                                                >
                                                  Undo Paid
                                                </Button>
                                              )}
                                              <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => handleLoginAs(m, user)}
                                              >
                                                Log In (S{m.season})
                                              </Button>
                                              {!m.isCommissioner && (
                                                <Button
                                                  size="sm"
                                                  variant="outline-danger"
                                                  onClick={() => handleRemoveMember(m.leagueId, m.userName, m.season)}
                                                  title={`Delete entry "${m.userName}" from Season ${m.season}`}
                                                >
                                                  🗑️ Remove Entry
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          <div className="text-center mt-4">
            <Link to="/admin/dashboard" className="btn btn-secondary">
              Back to Admin Dashboard
            </Link>
          </div>
        </Card.Body>
      </Card>

      {/* Admin Reset Password Modal */}
      <Modal show={Boolean(resetTargetUser)} onHide={() => setResetTargetUser(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password for {resetTargetUser?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetError && <Alert variant="danger">{resetError}</Alert>}
          {resetSuccess && <Alert variant="success">{resetSuccess}</Alert>}

          <div className="mb-3 text-muted small">
            <div><strong>Member:</strong> {resetTargetUser?.name}</div>
            <div><strong>Email:</strong> {resetTargetUser?.email || 'No email on file'}</div>
          </div>

          <Card className="mb-3 p-3 bg-light border">
            <h6 className="fw-bold mb-2">Option 1: Set New Password Directly</h6>
            <Form onSubmit={handleDirectPasswordReset}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">New Password</Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputGroup>
              </Form.Group>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={isResetting || !newPassword.trim()}
                className="w-100"
              >
                {isResetting ? <Spinner animation="border" size="sm" /> : 'Set New Password'}
              </Button>
            </Form>
          </Card>

          {resetTargetUser?.email && (
            <Card className="p-3 bg-light border">
              <h6 className="fw-bold mb-2">Option 2: Send Password Reset Link</h6>
              <p className="small text-muted mb-2">
                Sends a secure password reset link to <strong>{resetTargetUser.email}</strong>.
              </p>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSendResetEmail}
                disabled={isResetting}
                className="w-100"
              >
                {isResetting ? <Spinner animation="border" size="sm" /> : 'Send Reset Link to Email'}
              </Button>

              {generatedLink && (
                <div className="mt-3">
                  <Form.Label className="small fw-bold">Generated Reset Link:</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control readOnly value={generatedLink} />
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedLink);
                        alert('Reset link copied to clipboard!');
                      }}
                    >
                      Copy
                    </Button>
                  </InputGroup>
                </div>
              )}
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setResetTargetUser(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminUserManagement;